import { AbsgArrayHelper, CamelMapHelper } from '@absg/angular-common';
import { AssignedProductDetail, FormularyApi, MedicalEquivalentApi, ProductDetail } from '@absg/api-formulary';
import { InventoryItemSummary, ManagePositionApi, PendingInvItem, TransferApi, TransferDetail } from '@absg/api-inventory';
import { LookupDetail } from '@absg/api-lookup';
import { PatientLite, UserLite, UsersApi } from '@absg/api-party';
import {
  AssignmentHelperService, BaseService, ContainerTypes, PatientService as PartyPatientService, Position, PositionAssignment, Product, stationConstants,
  StorageStation,
} from '@absg/nim-client-shared';
import { Injectable, OnDestroy } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { concatMap, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Workflow } from 'shared.root/enums/workflow.enum';
import { PatientOwnedItemHelper } from 'shared.root/helpers/patient-owned-item-helper';
import { TransferLandingViewModel } from 'shared.root/models/transfer-view-landing.model';
import { SettingsService } from 'shared.root/services/settings.service';

import { LookupService } from './lookup.service';

@Injectable({ providedIn: 'root' })
export class PositionService extends BaseService implements OnDestroy {
  private station$: Observable<StorageStation>;
  private reloadCacheSubject = new Subject<void>();
  private positionsCache$: Observable<Position[]>;
  annexUsers: UserLite[];

  constructor(
    private stationInfoService: StationInfoService,
    private settingsService: SettingsService,
    private medicalEquivalentApi: MedicalEquivalentApi,
    private managePositionApi: ManagePositionApi,
    private transferApi: TransferApi,
    private assignmentHelperService: AssignmentHelperService,
    private usersApi: UsersApi,
    private lookupService: LookupService,
    private formularyApi: FormularyApi,
    private partyPatientService: PartyPatientService,
  ) {
    super();
    // Initialize the Observable variables
    this.station$ = this.stationInfoService.station.asObservable();
  }

  ngOnDestroy(): void {
    this.reloadCacheSubject.next();
    this.reloadCacheSubject.unsubscribe();
    this.positionsCache$ = null;
  }

  getPositionAssignments(): Observable<PositionAssignment[]> {
    this.stationInfoService.clearInventoryCache();

    return this.stationInfoService.station.asObservable()
      .pipe(concatMap((station: StorageStation) => this.managePositionApi.getStationAssignedItemsV2()
        .pipe(concatMap((stationInventory: InventoryItemSummary[]) => {
          const inclusionIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.supplierProductInclusionId);

          const detailsApi = inclusionIds.length > 0 ?
            this.formularyApi.getPracticeAssignedProductDetails(inclusionIds) : of([] as AssignedProductDetail[]);
          const patientIds = this.getPatientIdList(stationInventory);
          const patientApiCall = this.partyPatientService.getPatientListByIds(patientIds).pipe(take(1));

          return forkJoin([
            detailsApi,
            patientApiCall,
            this.lookupService.getPatientOwnedItemTypeDetailList().pipe(take(1)),
          ])
            .pipe(map(([formularies, patientInfo, lookupTypes]) => {
              let mappedPatientList: PatientLite[] = [];
              if (!!patientInfo && patientInfo.length > 0) {
                mappedPatientList = CamelMapHelper.camelizeKeys(patientInfo) as PatientLite[];
              }
              return this.convertToViewModel(station, stationInventory, formularies, mappedPatientList, lookupTypes);
            }));
        }))));
  }

  convertToViewModel(
    station: StorageStation,
    stationInventory: InventoryItemSummary[],
    formularies: AssignedProductDetail[],
    mappedPatientList: PatientLite[] = null,
    patientOwnedLookup: LookupDetail[] = null): PositionAssignment[] {

    // lookup data used in query below
    const formulariesById = AbsgArrayHelper.groupBy(formularies, f => f.supplierProductInclusionId);
    const inventoryByPosition = AbsgArrayHelper.groupBy(stationInventory, i => i.positionContainerId);
    // real work
    const positions: PositionAssignment[] = [];
    if (station.cabinets) {
      AbsgArrayHelper.each(station.cabinets, cabinet => {
        if (cabinet.children) {
          AbsgArrayHelper.each(cabinet.children, accessPoint => {
            if (accessPoint.name !== stationConstants.InactiveDoorLabel && accessPoint.children) {
              AbsgArrayHelper.each(accessPoint.children, (position) => {
                let isSimulate = false;
                if (position.containerType.id === ContainerTypes.LimitedAccessDrawer) {
                  const foundLad = AbsgArrayHelper.find(station.limitedAccessDrawers, (lad) => lad.id === position.id);
                  isSimulate = !!foundLad?.isSimulateMode;
                } else {
                  isSimulate = !!accessPoint.isSimulateMode;
                }
                const positionAssignment = this.assignmentHelperService.preparePositionAssignmentModelV3(position, accessPoint, formulariesById, inventoryByPosition, isSimulate);
                positionAssignment.cabinetNumber = cabinet.sequence;
                if (positionAssignment.isAssigned) {
                  const inventoryInPosition = inventoryByPosition.get(position.id.toString());
                  const invItems = AbsgArrayHelper.slice(inventoryInPosition);

                  positionAssignment.shouldTreatAsBin = this.settingsService.getBinSetting(position.id);
                  if (invItems && invItems.length > 1) {
                    // get equivalent products for this position
                    const equivalentIdsInPosition = AbsgArrayHelper.map(invItems, inv => inv.supplierProductInclusionId);
                    positionAssignment.assignedProductDetails = this.getMatchingProductDetails(equivalentIdsInPosition, formularies);
                  }
                  if (invItems[0]?.patientId && !!mappedPatientList) {
                    positionAssignment.patientInfo = mappedPatientList.find(x => x.patientId === invItems[0].patientId);
                    if (invItems[0].patientOwnedItemTypeId) {
                      positionAssignment.patientOwnedItemTypeShortName =
                        patientOwnedLookup.find(x => x.listInclusionId === invItems[0].patientOwnedItemTypeId)?.shortName;
                    }
                  }
                }

                positionAssignment.isWitnessRequired = this.settingsService.getWitnessRequiredSetting(
                  positionAssignment.positionContainerId, positionAssignment.supplierProductInclusionId);
                positions.push(positionAssignment);
              });
            }
          });
        }
      });
    }
    return positions;
  }

  getMatchingProductDetails(supplierInclusionIds: number[], productDetails: AssignedProductDetail[]): AssignedProductDetail[] {
    const matchingProducts = new Array<AssignedProductDetail>();
    const productsById = AbsgArrayHelper.groupBy(productDetails, p => p.supplierProductInclusionId);

    AbsgArrayHelper.each(supplierInclusionIds, supplierInclusionId => {
      const equivalentProduct = AbsgArrayHelper.first(productsById.get(supplierInclusionId.toString()));
      if (!AbsgArrayHelper.isNil(equivalentProduct)) {
        matchingProducts.push(equivalentProduct);
      }
    });
    return matchingProducts;
  }
  // END: Position Assignment CODE
  // Start: Remodel CODE
  /***
   * beginWorkflow - clear the cache because you are at the beginning of the workflow (Future Cache will likely be more targeted)
   * includeEmptyPositions - In rare cases where you want data from unassigned positions
   */
  getPositions(beginWorkflow = false, includeEmptyPositions = false, workflow = 0): Observable<Position[]> {
    if (beginWorkflow) {
      this.reloadCacheSubject.next();
      this.positionsCache$ = null;
    }
    if (!this.positionsCache$) {
      this.positionsCache$ = combineLatest([
        this.station$,
        this.managePositionApi.getStationAssignedItemsV2(),
      ]).pipe(
        concatMap(([station, stationInventory]) => {
          const inclusionIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.supplierProductInclusionId);
          const containerIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.positionContainerId);
          this.settingsService.stationContainerId = station.id;
          return forkJoin([
            this.productDetails$(inclusionIds),
            this.populateXRefs$(containerIds, inclusionIds),
            this.getPatientInfo(this.getPatientIdList(stationInventory)),
            this.lookupService.getPatientOwnedItemTypeDetailList().pipe(take(1)),
          ]).pipe(
            map(([formularies, __, patientInfo, lookupTypes]) => {
              let mappedPatientList: PatientLite[] = [];
              if (!AbsgArrayHelper.isNil(patientInfo) && patientInfo.length > 0) {
                mappedPatientList = CamelMapHelper.camelizeKeys(patientInfo) as PatientLite[];
              }
              const products = this.stationInfoService.convertAssignedProductDetailToProductDetail(formularies);
              return this.convertToPositionModel(station, stationInventory, products, includeEmptyPositions,
                mappedPatientList, lookupTypes, null, null, workflow);
            }),
          );
        }),
        takeUntil(this.reloadCacheSubject),
        shareReplay(1),
      );
    }
    return this.positionsCache$;
  }

  getTransferPositionItems(): Observable<Position[]> {
    return combineLatest([this.station$, this.managePositionApi.getStationAssignedItemsV2(), this.transferApi.getTransferTransactions(),
    this.lookupService.getPatientOwnedItemTypeDetailList().pipe(take(1))])
      .pipe(
        concatMap(([station, stationInventory, transferTransactions, lookupTypes]) => {
          if (transferTransactions && transferTransactions.length > 0) {
            let transferInclusionIds: number[] = AbsgArrayHelper.map(transferTransactions, i => i.supplierProductInclusionId);
            const requestedInclusionIds: number[] = AbsgArrayHelper.map(AbsgArrayHelper.filter(transferTransactions, i => i.requestedSupplierProductInclusionId != null),
              j => j.requestedSupplierProductInclusionId);
            transferInclusionIds = AbsgArrayHelper.union(transferInclusionIds, requestedInclusionIds);
            const inclusionIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.supplierProductInclusionId);
            const containerIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.positionContainerId);
            const submittedByIds: number[] = AbsgArrayHelper.map(transferTransactions, i => i.annexRequestCreatedBy);
            const modifiedByIds: number[] = AbsgArrayHelper.map(transferTransactions, i => i.cancelAnnexRequestCreatedBy);
            const userIds = AbsgArrayHelper.union(modifiedByIds, submittedByIds);
            this.settingsService.stationContainerId = station.id;
            const stationInventories: InventoryItemSummary[] = [];
            const unAssignedTransfer: TransferDetail[] = [];
            AbsgArrayHelper.each(stationInventory, item => {
              if (inclusionIds.indexOf(item.supplierProductInclusionId) >= 0) {
                stationInventories.push(item);
              }
            });

            AbsgArrayHelper.each(transferInclusionIds, id => {
              if (inclusionIds.indexOf(id) === -1) {
                inclusionIds.push(id);
              }
            });

            AbsgArrayHelper.each(transferTransactions, item => {
              const foundItem = AbsgArrayHelper.find(stationInventories, i => i.supplierProductInclusionId === item.supplierProductInclusionId
                && i.patientId === item.patientId && i.patientOwnedItemTypeId === item.patientOwnedItemTypeId);
              if (!foundItem) {
                unAssignedTransfer.push(item);
              }
            });
            return forkJoin([this.productDetails$(inclusionIds).pipe(take(1)),
            this.usersApi.getUserBasicInformationList(userIds).pipe(take(1)),
            this.getPatientInfo(this.getPatientIdList(stationInventory, unAssignedTransfer)),
            this.populateXRefs$(containerIds, inclusionIds).pipe(take(1)),
            ]).pipe(
              map(([formularies, users, patientInfo]) => {
                const annexUsers = CamelMapHelper.camelizeKeys(users) as UserLite[];
                this.setAnnexUsers(annexUsers);
                let mappedPatientList: PatientLite[] = [];
                if (!AbsgArrayHelper.isNil(patientInfo) && patientInfo.length > 0) {
                  mappedPatientList = CamelMapHelper.camelizeKeys(patientInfo) as PatientLite[];
                }
                const products = this.stationInfoService.convertAssignedProductDetailToProductDetail(formularies);
                return this.convertToPositionModel(station, stationInventories, products, false, mappedPatientList, lookupTypes,
                  transferTransactions, unAssignedTransfer, Workflow.Transfer);
              }),
            );
          } else {
            return of([]);
          }
        }));
  }

  getPatientIdList(inventoryItem: InventoryItemSummary[], transferItems: TransferDetail[] = []): number[] {
    const patientIds = [];
    for (const position of inventoryItem) {
      if (inventoryItem && position?.patientId) {
        patientIds.push(position?.patientId);
      }
    }
    for (const transferItem of transferItems) {
      if (transferItems && transferItem?.patientId) {
        patientIds.push(transferItem?.patientId);
      }
    }
    return patientIds;
  }

  setAnnexUsers(users: UserLite[]): void {
    this.annexUsers = users;
  }

  getAnnexUsers(): UserLite[] {
    return this.annexUsers;
  }

  filterPositions(positions: Position[], filterValue: string): Position[] {
    filterValue = filterValue.toLowerCase();
    const filteredArray = positions.filter(position => this.isItemMatch(position, filterValue));
    return filteredArray;
  }

  /***
  * Returns the cheapest Item's ProductDetail
  */
  getCheapestAutoReorderProductDetailForPosition(position: Position): Observable<ProductDetail> {
    return this.getPositions().pipe(
      concatMap(positions => {
        // Do not include any items assigned elsewhere in the station
        const excludeList = AbsgArrayHelper.map(
          AbsgArrayHelper.flatMap(
            AbsgArrayHelper.filter(positions, p => p.getContainerId() !== position.getContainerId()),
            pos => pos.products),
          prod => {
            if (!prod?.detail?.supplierProductInclusionId) {
              this.handleErrorMessage(`Record is missing in Producthelper table for the item with inventoryItemId (${prod.inventoryItemId}) in the current practice.`);
            }
            return prod?.detail?.supplierProductInclusionId;
          });
        const request = this.stationInfoService.generateGetCheapestEquivilantRequest(excludeList.filter((a) => a !== undefined));
        return this.medicalEquivalentApi.getCheapestEquivilantForProduct(position.products[0].detail.productId, request)
          .pipe(take(1), map(productDetail => productDetail));
      }),
    );
  }

  /***
  * sorts items based on position, displayname and itemId
  */
  sortFlattenedTransferPositions(transfers: TransferLandingViewModel[]): TransferLandingViewModel[] {
    return AbsgArrayHelper.sortBy(transfers, [
      'position.storagePosition.parentContainerName',
      'position.storagePosition.sequence',
      (item) => !(item.position.products[0].detail.itemId === item.position.preferredItemId),
      (item) => !item.position.products[0].detail.isPracticeFavorite,
      'position.products[0].detail.displayName',
      'position.products[0].detail.itemId',
    ]);
  }

  /***
  * sorts items based on position, displayname and itemId
  */
  sortFlattenedPositions(positions: Position[]): Position[] {
    return AbsgArrayHelper.sortBy(positions, [
      'storagePosition.cabinetSort',
      'storagePosition.doorNumber',
      'storagePosition.sequence',
      (item) => !(item.products[0].detail.itemId === item.preferredItemId),
      (item) => !item.products[0].detail.isPracticeFavorite,
      'products[0].detail.displayName',
      'products[0].detail.itemId',
    ]);
  }

  /***
  * flattened position model for display
  */
  getFlattenedPositionModelForDisplay(positions: Position[]): Position[] {
    const flattenedPositions: Position[] = [];
    AbsgArrayHelper.each(positions, position => {
      AbsgArrayHelper.each(position.products, (product) => {
        const clonedPosition: Position = AbsgArrayHelper.cloneDeep(position);
        clonedPosition.products = [];
        clonedPosition.products.push(product);
        flattenedPositions.push(clonedPosition);
      });
    });

    return flattenedPositions;
  }

  private convertToPositionModel(
    station: StorageStation,
    stationInventory: InventoryItemSummary[],
    formularies: ProductDetail[],
    includeEmptyPosition: boolean,
    mappedPatientList: PatientLite[] = null,
    patientOwnedLookup: LookupDetail[] = null,
    transferDetail: TransferDetail[] = null,
    unAssignedTransfer: TransferDetail[] = null,
    workflow = 0): Position[] {
    // lookup data used in query below
    const formulariesById = AbsgArrayHelper.groupBy(formularies, f => f.supplierProductInclusionId);
    const inventoryByPosition = AbsgArrayHelper.groupBy(stationInventory, i => i.positionContainerId);
    const containerIds: number[] = AbsgArrayHelper.map(stationInventory, i => i.positionContainerId);
    // real work
    const positions: Position[] = [];
    if (station.cabinets) {
      AbsgArrayHelper.each(station.cabinets, cabinet => {
        if (cabinet.children) {
          AbsgArrayHelper.each(cabinet.children, accessPoint => {
            if (accessPoint.name !== stationConstants.InactiveDoorLabel && accessPoint.children && accessPoint.children.length > 0) {
              const storagePositions = includeEmptyPosition ? accessPoint.children :
                AbsgArrayHelper.filter(accessPoint.children, i => containerIds.indexOf(i.id) !== -1);
              AbsgArrayHelper.each(storagePositions, (storagePosition) => {
                const position = new Position();
                position.storagePosition = storagePosition;
                position.storagePosition.isLad = (storagePosition.containerType.id === ContainerTypes.LimitedAccessDrawer);
                if (position.storagePosition.isLad) {
                  const foundLad = AbsgArrayHelper.find(station.limitedAccessDrawers, (lad) => lad.id === position.storagePosition.id);
                  position.storagePosition.isSimulateMode = !!foundLad?.isSimulateMode;
                } else {
                  position.storagePosition.isSimulateMode = !!accessPoint.isSimulateMode;
                }
                position.storagePosition.cabinetSort = this.getCabinetSort(cabinet.containerType.id);
                // Most of the time we don't care about empty positions
                const summaries = AbsgArrayHelper.slice(inventoryByPosition.get(storagePosition.id.toString()));
                if (!includeEmptyPosition && summaries.length === 0) {
                  return;
                }
                if (summaries[0]?.patientId && !AbsgArrayHelper.isNil(mappedPatientList)) {
                  position.patientInfo = mappedPatientList.find(x => x.patientId === summaries[0].patientId);
                  const patientOwnedLookupType = patientOwnedLookup.find(x => x.listInclusionId === summaries[0]?.patientOwnedItemTypeId);
                  if (summaries[0]?.patientOwnedItemTypeId && !AbsgArrayHelper.isNil(summaries[0]?.patientOwnedItemTypeId)) {
                    position.patientOwnedItemTypeShortName = patientOwnedLookupType.shortName;
                    position.patientOwnedItemTypeFullName = patientOwnedLookupType.name;
                    position.patientOwnedItemTypeId = patientOwnedLookupType.listInclusionId;
                  }
                }
                position.products = AbsgArrayHelper.map(summaries, (summary) => {
                  const product = new Product();
                  product.inventoryItemId = summary.inventoryItemId;
                  product.pendingInventory = {
                    stationContainerId: summary.stationContainerId,
                    supplierProductInclusionId: summary.supplierProductInclusionId,
                    pendingQuantity: summary.pendingQuantity,
                    pendingOverstockQuantity: summary.pendingOverstockQuantity,
                    pendingAnnexTransferQuantity: summary.pendingAnnexTransferQuantity,
                  } as PendingInvItem;
                  if (!!summary.supplierProductInclusionId) {
                    product.detail = AbsgArrayHelper.first(formulariesById.get(summary.supplierProductInclusionId.toString()));
                    product.displayJcode = product.detail ? (product.detail.altJCode || product.detail.jCode) : '';
                  }
                  product.lots = summary.inventoryItemProducts;
                  if (transferDetail && transferDetail.length > 0) {
                    product.transferItems = AbsgArrayHelper.filter(transferDetail,
                      i => i.supplierProductInclusionId === summary.supplierProductInclusionId && i.patientId === summary.patientId
                        && i.patientOwnedItemTypeId === summary.patientOwnedItemTypeId);
                    if (product.transferItems.length > 0) {
                      const requestItems = AbsgArrayHelper.filter(product.transferItems, t => t.requestedSupplierProductInclusionId != null);
                      if (requestItems.length) {
                        AbsgArrayHelper.each(requestItems, requestItem => {
                          requestItem.substitutedItemDetail = AbsgArrayHelper.first(formulariesById[requestItem.requestedSupplierProductInclusionId]);
                        });
                      }
                    }
                  }
                  product.inventoryItemId = summary.inventoryItemId;
                  return product;
                });
                if (position.products.length > 0) {
                  this.populateSettings(position, workflow);
                }
                positions.push(position);
              });
            }
          });
        }
      });
    }
    // Add Unassigned Transfer Item to the Position to be displayed in the Transfer Landing page
    if (!AbsgArrayHelper.isNil(unAssignedTransfer) && unAssignedTransfer.length > 0) {
      AbsgArrayHelper.each(unAssignedTransfer, (item) => {
        const itemDetail = AbsgArrayHelper.find(formularies, i => i.supplierProductInclusionId === item.supplierProductInclusionId);
        const transferItemDetails = AbsgArrayHelper.filter(transferDetail, i => i.supplierProductInclusionId === item.supplierProductInclusionId
          && i.transferOutTransactionId === item.transferOutTransactionId && i.patientId === item.patientId && i.patientOwnedItemTypeId === item.patientOwnedItemTypeId);
        if (!AbsgArrayHelper.isNil(itemDetail) && !AbsgArrayHelper.isNil(transferItemDetails)) {
          positions.push(this.createUnassignedPosition(itemDetail, transferItemDetails, formularies, mappedPatientList, patientOwnedLookup));
        }
      });
    }
    return positions;
  }

  private createUnassignedPosition(itemDetail: ProductDetail, transferItemDetail: TransferDetail[], formularies: ProductDetail[],
    mappedPatientList: PatientLite[], patientOwnedLookup: LookupDetail[]): Position {
    const position = new Position();
    position.storagePosition = null;
    const product = new Product();
    product.detail = itemDetail;
    product.displayJcode = itemDetail.altJCode || itemDetail.jCode;
    product.transferItems = transferItemDetail;
    product.lots = [];
    product.pendingInventory = null;
    const formulariesById = AbsgArrayHelper.groupBy(formularies, f => f.supplierProductInclusionId);
    const requestItems = AbsgArrayHelper.filter(product.transferItems, t => t.requestedSupplierProductInclusionId != null);
    if (requestItems.length) {
      AbsgArrayHelper.forEach(requestItems, requestItem => {
        requestItem.substitutedItemDetail = AbsgArrayHelper.first(formulariesById.get(requestItem.requestedSupplierProductInclusionId.toString()));
      });
    }
    position.products = [product];

    for (const pd of position.products) {
      const patientInfo = mappedPatientList.find(x => x.patientId === pd.transferItems[0].patientId);
      if (patientInfo?.patientId > 0) {
        position.patientInfo = patientInfo;
        const patientOwnedLookupType = patientOwnedLookup.find(x => x.listInclusionId === pd.transferItems[0].patientOwnedItemTypeId);
        position.patientOwnedItemTypeId = patientOwnedLookupType?.listInclusionId;
        position.patientOwnedItemTypeShortName = patientOwnedLookupType?.shortName;
        position.patientOwnedItemTypeFullName = patientOwnedLookupType?.name;
      }
    }

    return position;
  }

  private populateSettings(position: Position, workflow: number): void {
    const containerId = position.getContainerId();
    // Bin Settings
    position.isBin = this.settingsService.getBinSetting(containerId);
    position.preferredItemId = this.settingsService.getPreferredItemIdSetting(containerId);
    // TODO: We need to get the Bin supplierProductInclusionId after practice formulary is done
    const formularyId = position.isBin ? position.products[0]?.detail?.meiParentId : position.products[0]?.detail?.supplierProductInclusionId;
    const formularyTypeId = position.products[0]?.detail?.formularyTypeId;
    const isPatientOwned = position.isPatientOwnedStatus();

    position.dispenseTypeId = this.settingsService.getDispenseTypeIdSetting(containerId);
    position.lotTrackingRequired = this.settingsService.getLotTrackingSetting(containerId);

    // Witness Settings
    position.isWitnessRequired = this.settingsService.getWitnessRequiredSetting(containerId, formularyId);
    position.isVerifyQOHRequired = this.settingsService.getVerifyQOHSetting(containerId, formularyId);
    // Auto Reorder Settings
    position.autoReorder = this.settingsService.getAutoReOrderSetting(containerId, formularyId, formularyTypeId, isPatientOwned);
    position.autoReorderMin = this.settingsService.getMinSetting(containerId);
    position.autoReorderMax = this.settingsService.getMaxSetting(containerId);
    if (workflow === Workflow.Transfer) {
      // Print Settings
      position.printReceiptForCancelTransfer = this.settingsService.getReceiptForCancelTransferSetting(containerId, formularyId);
      position.printReceiptForTransferIn = this.settingsService.getReceiptForTransferInSetting(containerId, formularyId);
      position.printReceiptForTransferOut = this.settingsService.getReceiptForTransferOutSetting(containerId, formularyId);
      // Reason Settings
      position.reasonForTransferDiscrepancyRequired = this.settingsService.getReasonForTransferDiscrepancySetting(containerId, formularyId);
      position.bothReasonsForTransferDiscrepancyRequired =
        this.settingsService.getReasonForTransferDiscrepancyRequireBothSetting(containerId, formularyId);
    } else {
      // Print Settings
      position.printReceiptForClinicCharge = this.settingsService.getReceiptForClinicChargeSetting(containerId, formularyId);
      position.printReceiptForDispense = this.settingsService.getReceiptForDispenseSetting(containerId, formularyId);
      position.printReceiptForRestock = this.settingsService.getReceiptForRestockSetting(containerId, formularyId);
      position.printReceiptForReturn = this.settingsService.getReceiptForReturnSetting(containerId, formularyId);
      position.printReceiptForWaste = this.settingsService.getReceiptForWasteSetting(containerId, formularyId);
      // Reason Settings
      position.reasonForClinicChargeRequired = this.settingsService.getReasonForClinicChargeSetting(containerId, formularyId);
      position.reasonForDiscrepancyRequired = this.settingsService.getReasonForDiscrepancySetting(containerId, formularyId);
      position.reasonForReturnRequired = this.settingsService.getReasonForReturnSetting(containerId, formularyId);
      position.reasonForWasteRequired = this.settingsService.getReasonForWasteSetting(containerId, formularyId);
      position.bothReasonsForDiscrepancyRequired = this.settingsService.getReasonForDiscrepancyRequireBothSetting(containerId, formularyId);
      position.useOverridePrice = this.settingsService.getOverridePriceSetting(formularyId);
      if (position.useOverridePrice) {
        position.overridePrice = this.settingsService.getOverridePriceValue(formularyId);
      }
    }
  }

  private productDetails$(inclusionIds: number[]): Observable<AssignedProductDetail[]> {
    if (inclusionIds.length === 0) {
      return of([]);
    }

    return this.formularyApi.getPracticeAssignedProductDetails(inclusionIds);
  }
  private populateXRefs$(containerIds: number[], inclusionIds: number[]): Observable<void> {
    if (containerIds.length === 0 && inclusionIds.length === 0) {
      return of(void 0);
    }
    return this.settingsService.populateXrefs(containerIds, inclusionIds);
  }

  private isItemMatch(position: Position, filterValue: string): boolean {
    if (AbsgArrayHelper.isNil(filterValue) || filterValue === '') {
      return true;
    }

    return this.searchFormularyFields(position, filterValue);
  }

  private searchFormularyFields(position: Position, filterValue: string): boolean {
    return AbsgArrayHelper.some(position.products, product => {
      const detail = product.detail;
      if (!detail) {
        return false;
      }
      if ((detail.alternateName && detail.alternateName.toLowerCase().includes(filterValue))
        || (detail.displayName && detail.displayName.toLowerCase().includes(filterValue))
        || (detail.unitNdc && detail.unitNdc.toLowerCase().includes(filterValue))
        || (detail.itemId && detail.itemId.toLowerCase().includes(filterValue))
        || (detail.altJCode && detail.altJCode.toLowerCase().includes(filterValue))
        || (detail.jCode && detail.jCode.toLowerCase().includes(filterValue))
        || PatientOwnedItemHelper.searchPatientFields(position.patientInfo, filterValue)) {
        return true;
      }
    });
  }

  private getCabinetSort(containerTypeId): number {
    let returnValue;
    switch (containerTypeId) {
      case ContainerTypes.Virtual:
        returnValue = 3;
        break;
      case ContainerTypes.Refrigerator:
        returnValue = 2;
        break;
      default:
        returnValue = 1;
        break;
    }
    return returnValue;
  }

  private getPatientInfo(patientIds: number[]): Observable<PatientLite[]> {
    return this.partyPatientService.getPatientListByIds(patientIds).pipe(take(1));
  }
}
